import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Loader2, Eye, Search, Users,
  ChevronLeft, ChevronRight
} from 'lucide-react';

export const AdminCustomers = () => {
  const { token } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString()
        });
        if (search) {
          params.append('search', search);
        }

        const response = await fetch(`${backendUrl}/api/admin/customers?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCustomers(data.customers);
          setTotal(data.total);
        }
      } catch (error) {
        console.error('Customers error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [token, backendUrl, page, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Kunden</h1>
          <p className="text-slate-400 mt-1">{total} Kunden insgesamt</p>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Suche nach E-Mail, Name oder Firma..."
                className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Suchen
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : customers.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Kunden gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Kunde</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Firma</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">E-Mail</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Erstellt</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-4 px-4">
                        <span className="text-white font-medium">
                          {customer.first_name} {customer.last_name}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-300">
                          {customer.company || '-'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-300">{customer.email}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-400 text-sm">
                          {new Date(customer.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button asChild variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                          <Link to={`/admin/customers/${customer.id}`}>
                            <Eye className="h-4 w-4 mr-1" /> Details
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
              <span className="text-slate-400 text-sm">
                Seite {page + 1} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
